<template>
    <MainComponent ref="sectionRef">
        <template #title>
            <h2>{{ $t('profile.section.task.list.title') }}</h2>
        </template>
        <template v-if="!allTasksCompleted" #subtitle>
            {{ $t('profile.section.task.list.subtitle') }}
        </template>
        <template #title-action>
            <VJoyTag variant="inactive" size="small">
                <VJoyIcon name="eye" size="xsmall" />
                {{ $t('profile.section.only.visible.to.you') }}
            </VJoyTag>
        </template>
        <template #content>
            <div v-if="!allTasksCompleted" data-testid="task-list-section-tasks">
                <VJoyWrapper direction="column" row-gap="6" align="flex-start" no-margin>
                    <VJoyWrapper
                        ref="tasksEl"
                        class="task-list-section__tasks"
                        direction="row"
                        column-gap="4"
                        align="flex-start"
                        wrap="nowrap"
                        no-margin
                        tabindex="0"
                    >
                        <TaskListItem
                            v-for="task in tasksInfo"
                            :key="task.id"
                            :task="task"
                            data-testid="task-list-item"
                            @confetti-animation-ended="handleConfettiAnimationEnded"
                        />
                    </VJoyWrapper>
                    <VJoyWrapper class="task-list-section__progress-bar-container" direction="row" column-gap="4" wrap="nowrap" no-margin>
                        <TaskListProgressBar class="task-list-section__progress-bar" :tasks="tasksInfo" />
                        <VJoyIconButton
                            data-testid="task-list-section-button-chevron-left"
                            variant="ghost"
                            icon="chevron-left"
                            size="small"
                            :label="t('profile.section.task.list.scroll.left')"
                            @click="scrollTasks(-1)"
                        />
                        <VJoyIconButton
                            data-testid="task-list-section-button-chevron-right"
                            variant="ghost"
                            icon="chevron-right"
                            size="small"
                            :label="t('profile.section.task.list.scroll.right')"
                            @click="scrollTasks(1)"
                        />
                    </VJoyWrapper>
                </VJoyWrapper>
            </div>
            <TaskListCompleted v-else data-testid="task-list-completed" :tasks-info="tasksInfo" />
        </template>
    </MainComponent>
</template>

<script setup lang="ts">
    import {type ComponentPublicInstance, computed, onBeforeUnmount, onMounted, useTemplateRef, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {navigateTo, useAsyncData, useDataTracking, useTranslation} from '#imports';
    import {launchConfetti} from '@malt/vue-animation';
    import {pushVJoySnackbar, VJoyIcon, VJoyIconButton, VJoyTag, VJoyWrapper} from '@maltjoy/core-vue';
    import MainComponent from '~/components/main/common/MainComponent.vue';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint, TrackingEvents} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';
    import TaskListCompleted from './TaskListCompleted.vue';
    import TaskListItem from './TaskListItem.vue';
    import TaskListProgressBar from './TaskListProgressBar.vue';
    import {useTaskListStore} from '../store/task-list.store';
    import type {TaskListItemType} from '../types/TaskListItemType';

    // ##### Tasks
    const {getTasks} = useTaskListStore();
    const {data: tasks, refresh: refreshTasks} = useAsyncData('tasks', async () => await getTasks());

    // ##### Tasks info
    const {t} = useTranslation();
    const {track} = useDataTracking();
    const {experiences} = storeToRefs(useProfileStore());
    const allTasksCompleted = computed(() => tasksInfo.value.every((task) => task.completed));
    const tasksInfo = computed<TaskListItemType[]>(() => {
        const tasksInfo: TaskListItemType[] = [
            {
                id: 'picture',
                icon: 'profile-picture',
                title: t('profile.section.task.list.item.picture.title'),
                description: t('profile.section.task.list.item.picture.subtitle'),
                completed: tasks.value?.completedPhoto,
                trackingKey: EditionOpenedEntryPoint.PROFILE_TASK_LIST_PICTURE,
                relatedUpdateEvent: 'photo:updated',
                onClick: () => {
                    eventBus.emit('photo:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_TASK_LIST_PICTURE});
                },
            },
            {
                id: 'experiences',
                icon: 'briefcase',
                title: t('profile.section.task.list.item.experiences.title'),
                description: t('profile.section.task.list.item.experiences.subtitle'),
                completed: tasks.value?.completedExperience,
                trackingKey: EditionOpenedEntryPoint.PROFILE_TASK_LIST_2_EXPERIENCES,
                relatedUpdateEvent: 'experiences:updated',
                onClick: () => {
                    return navigateTo({
                        path: `/profile/experiences/new`,
                        query: {entryPoint: EditionOpenedEntryPoint.PROFILE_TASK_LIST_2_EXPERIENCES},
                    });
                },
            },
            {
                id: 'description',
                icon: 'user-edit',
                title: t('profile.section.task.list.item.description.title'),
                description: t('profile.section.task.list.item.description.subtitle'),
                completed: tasks.value?.completedDescription,
                trackingKey: EditionOpenedEntryPoint.PROFILE_TASK_LIST_DESCRIPTION,
                relatedUpdateEvent: 'about:updated',
                onClick: () => {
                    eventBus.emit('about:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_TASK_LIST_DESCRIPTION});
                },
            },
            {
                id: 'top-skills',
                icon: 'color',
                title: t('profile.section.task.list.item.top.skills.title'),
                description: t('profile.section.task.list.item.top.skills.subtitle'),
                completed: tasks.value?.completedTopSkills,
                trackingKey: EditionOpenedEntryPoint.PROFILE_TASK_LIST_TOP_SKILLS,
                relatedUpdateEvent: 'header:updated',
                onClick: () => {
                    eventBus.emit('header:edit-mode', {
                        entryPoint: EditionOpenedEntryPoint.PROFILE_TASK_LIST_TOP_SKILLS,
                        scrollToElement: 'edit-header-top-skills',
                    });
                },
            },
            {
                id: 'skills-in-experiences',
                icon: 'conditions',
                title: t('profile.section.task.list.item.skills.in.experiences.title'),
                description: t('profile.section.task.list.item.skills.in.experiences.subtitle'),
                completed: tasks.value?.completedSkillsOnExperience,
                trackingKey: EditionOpenedEntryPoint.PROFILE_TASK_LIST_SKILL_ATTACHED,
                relatedUpdateEvent: 'experiences:updated',
                onClick: () => {
                    if ((experiences.value?.items.length ?? 0) <= 0) {
                        return navigateTo({
                            path: '/profile/experiences/new',
                            query: {entryPoint: EditionOpenedEntryPoint.PROFILE_TASK_LIST_SKILL_ATTACHED},
                        });
                    } else {
                        const lastExperienceId = experiences.value!.items[0].id;
                        return navigateTo({
                            path: `/profile/experiences/${lastExperienceId}`,
                            query: {entryPoint: EditionOpenedEntryPoint.PROFILE_TASK_LIST_SKILL_ATTACHED},
                        });
                    }
                },
            },
            {
                id: 'code-of-conduct',
                icon: 'edit-document',
                title: t('profile.section.task.list.item.code.of.conduct.title'),
                description: t('profile.section.task.list.item.code.of.conduct.subtitle'),
                completed: tasks.value?.completedCharter,
                trackingKey: EditionOpenedEntryPoint.PROFILE_TASK_LIST_CODE_CONDUCT,
                onClick: () => {
                    return navigateTo({path: '/profile/freelancer-charter'});
                },
            },
        ];
        return tasksInfo.sort((a, b) => (a.completed ? 1 : b.completed ? -1 : 0));
    });

    // ##### Navigation
    const SCROLL_AMOUNT = 280 + 16; // should match the width of a task item + gap
    const tasksEl = useTemplateRef<ComponentPublicInstance>('tasksEl');
    function scrollTasks(delta: number) {
        tasksEl.value?.$el?.scrollBy({
            left: delta * SCROLL_AMOUNT,
            behavior: 'smooth',
        });
    }

    // ##### Tracking
    const TASK_EDITION_SECTION: Record<string, string> = {
        picture: 'photo',
        experiences: 'experiences',
        description: 'description',
        'top-skills': 'skill_set',
        'skills-in-experiences': 'experiences',
        'code-of-conduct': 'charter',
    };

    onMounted(() => {
        const pendingTasks = tasksInfo.value.filter((task) => !task.completed);
        if (pendingTasks.length > 0) {
            track(TrackingEvents.SUGGESTED_FOR_YOU_VIEWED, {
                number_of_cards: pendingTasks.length,
                cards_displayed: pendingTasks.map((task) => task.trackingKey),
                page_type: 'profile',
            });
        }
        tasksInfo.value.forEach((task) => {
            track(TrackingEvents.PROFILE_EDITION_NUDGE_VIEWED, {
                nudge_type: 'profile_task_list_item',
                card_name: task.trackingKey,
                is_card_checked: task.completed ?? false,
                profile_edition_section: TASK_EDITION_SECTION[task.id],
                page_type: 'profile',
            });
        });
    });

    // ##### Confettis animations
    onMounted(async () => {
        updateLocalStorage();
        await triggerConfettisOnSectionIfNeeded();
    });
    watch(tasksInfo, async () => {
        updateLocalStorage();
        await triggerConfettisOnSectionIfNeeded();
    });
    function updateLocalStorage() {
        const tasksNotCompleted = tasksInfo.value.filter((task) => !task.completed).map((task) => task.id);
        const currentTaskListItemsNeedsConfettis = JSON.parse(localStorage.getItem('task-list-items-needs-confettis') ?? '[]');
        const newTaskListItemsNeedsConfettis = Array.from(new Set([...currentTaskListItemsNeedsConfettis, ...tasksNotCompleted]));
        localStorage.setItem('task-list-items-needs-confettis', JSON.stringify(newTaskListItemsNeedsConfettis));
    }
    const sectionRef = useTemplateRef<ComponentPublicInstance>('sectionRef');
    async function triggerConfettisOnSectionIfNeeded() {
        const someTasksItemsNeededConfettis = JSON.parse(localStorage.getItem('task-list-items-needs-confettis') ?? '[]').length > 0;
        const isAllTasksNowCompleted = tasksInfo.value.every((task) => task.completed);
        if (someTasksItemsNeededConfettis && isAllTasksNowCompleted) {
            eventBus.emit('task-list:completed');
            await new Promise((resolve) => setTimeout(resolve, 500));
            launchConfetti(sectionRef.value?.$el, {
                confettiOptions: {
                    particleCount: 400,
                    origin: {y: 1.65},
                    startVelocity: 50,
                    spread: 120,
                },
            });
            localStorage.setItem('task-list-items-needs-confettis', '[]');
        }
    }
    function handleConfettiAnimationEnded() {
        scrollTasks(-99);
    }

    // ##### Refresh tasks
    async function refreshTasksHandler() {
        await refreshTasks();
    }

    function createEventHandlersForTaskCardsUpdate() {
        tasksInfo.value.forEach((task) => {
            if (task.relatedUpdateEvent) {
                eventBus.on(task.relatedUpdateEvent, refreshTasksHandler);
            }
        });
    }
    onMounted(() => {
        createEventHandlersForTaskCardsUpdate();
    });

    function stopEventHandlersForTaskCardsUpdate() {
        tasksInfo.value.forEach((task) => {
            if (task.relatedUpdateEvent) {
                eventBus.off(task.relatedUpdateEvent, refreshTasksHandler);
            }
        });
    }
    onBeforeUnmount(() => {
        stopEventHandlersForTaskCardsUpdate();
    });

    // ##### Charter signed
    onMounted(() => {
        if (sessionStorage.getItem('charterSigned') === 'true') {
            pushVJoySnackbar({
                props: {
                    message: t('profile.section.task.list.completed.charter.signed'),
                    header: t('profile.section.task.list.completed.charter.signed.header'),
                    level: 'success',
                },
            });
            sessionStorage.removeItem('charterSigned');
        }
    });
</script>

<style scoped lang="scss">
    .task-list-section {
        &__tasks {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior-x: contain;
            scrollbar-width: none;
            -ms-overflow-style: none;
            &::-webkit-scrollbar {
                display: none;
            }
        }

        &__progress-bar-container {
            width: 100%;
        }
    }
</style>
